import {
  BasicItem,
  type FormData as ItemFormData,
  Project,
  ReportingItem,
} from "vmd-types";

import { STRAPI_URL } from "../env";
import { invalidateAppQueries } from "./query-cache";

/**
 * BFF endpoint for updating tracking items (questionnaire forms).
 * Updates BasicItem columns, ReportingItem columns, formData, or any combination:
 * a question with an entity_ref lands on its master data column, every other
 * question lands in formData.
 * Returns the complete updated Project to keep cache in sync.
 *
 * @param projectId The project ID
 * @param reportingItemId The ReportingItem ID
 * @param updates Object with optional basicItem, reportingItem and/or formData updates
 */
export async function updateTrackingItem(
  projectId: string,
  reportingItemId: string,
  updates: {
    basicItem?: Partial<BasicItem>;
    reportingItem?: Partial<ReportingItem>;
    formData?: ItemFormData;
    /** Set only by the Submit button; moves the review to the submitted stage. */
    submitted?: boolean;
  }
): Promise<Project | undefined> {
  try {
    const response = await fetch(
      `${STRAPI_URL}/project/${projectId}/tracking-items/${reportingItemId}`,
      {
        body: JSON.stringify(updates),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
      }
    );

    if (!response.ok) {
      throw new Error(
        `update tracking-item failed with status ${response.status}.`
      );
    }
    const updatedProject = (await response.json()) as Project;
    await invalidateAppQueries(["project"]);
    await invalidateAppQueries(["projects"]);
    await invalidateAppQueries(["overlay"]);
    await invalidateAppQueries(["overview"]);
    await invalidateAppQueries(["dashboard"]);
    return updatedProject;
  } catch {
    return undefined;
  }
}
